import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, ExternalLink, Utensils, Users, Truck, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

// Living Fruit Outreach gallery images
const outreachGalleryImages = [
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/5ionxli2_IMG_6138%20%282%29.jpg',
    title: 'Community Food Distribution',
    description: 'Living Fruit Ministry volunteers distributing food to seniors'
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/n47nospi_IMG_6139%20%281%29.jpg',
    title: 'Volunteers in Action',
    description: 'Our dedicated volunteers with food supplies at the park'
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/y5q52p3a_IMG_6148.jpg',
    title: 'Senior Outreach',
    description: 'Delivering essential supplies to seniors in our community'
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/jtwdd824_IMG_6145%20%281%29.jpg',
    title: 'Kids Outreach Supplies',
    description: 'Activity kits and toys for children in need'
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/m7xor1cm_IMG_6143%20%281%29.jpg',
    title: 'Supply Distribution',
    description: 'Volunteers loading supplies for community delivery'
  }
];

export default function OutreachMissionPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="outreach-mission-page">
      {/* Banner */}
      <div className="w-full">
        <img src={MINISTRY_BANNER} alt="Living Fruit Ministry" className="w-full h-auto" />
      </div>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-green-50 to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <div className="inline-flex items-center gap-2 bg-green-100 px-4 py-2 rounded-full">
                <Heart size={18} className="text-green-600" />
                <span className="text-sm font-medium text-green-800">501(c)(3) Nonprofit Organization</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-green-100 px-4 py-2 rounded-full">
                <Heart size={18} className="text-green-600" />
                <span className="text-sm font-medium text-green-800">509(a)(2) Public Charity</span>
              </div>
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6">
              Living Fruit Outreach
            </h1>
            
            <p className="text-lg md:text-xl text-charcoal/70 max-w-2xl mx-auto mb-8">
              Serving our community through food assistance, disaster relief, and compassionate outreach programs. 
              Together, we're making a difference one family at a time.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-green-200 max-w-xl mx-auto">
              <h2 className="font-serif text-2xl text-charcoal mb-4">Visit Our Website</h2>
              <p className="text-charcoal/70 mb-6">
                Living Fruit Outreach now has its own dedicated website where you can learn more about our programs, 
                volunteer opportunities, and make donations directly to support our outreach mission.
              </p>
              
              <a 
                href="https://livingfruitoutreach.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
                data-testid="outreach-website-link"
              >
                <span>Go to livingfruitoutreach.com</span>
                <ExternalLink size={20} />
              </a>
              
              <p className="text-sm text-charcoal/50 mt-4">
                Opens in a new tab
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="font-serif text-3xl text-charcoal mb-4">
              Our Impact in Action
            </h2>
            <p className="text-charcoal/70">
              See how your support helps us serve families in our community
            </p>
          </motion.div>

          {/* Main Gallery Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-xl mb-4">
            <motion.img
              key={currentImageIndex}
              src={outreachGalleryImages[currentImageIndex]?.url}
              alt={outreachGalleryImages[currentImageIndex]?.title || 'Outreach photo'}
              className="w-full h-[400px] md:h-[500px] object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            />
            
            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev - 1 + outreachGalleryImages.length) % outreachGalleryImages.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
            >
              <ChevronLeft size={28} />
            </button>
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev + 1) % outreachGalleryImages.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
            >
              <ChevronRight size={28} />
            </button>
            
            {/* Image Caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
              <p className="text-white font-medium text-lg">{outreachGalleryImages[currentImageIndex]?.title}</p>
              <p className="text-white/70 text-sm">{outreachGalleryImages[currentImageIndex]?.description}</p>
            </div>
          </div>
          
          {/* Thumbnails */}
          <div className="flex gap-3 justify-center flex-wrap">
            {outreachGalleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-3 transition-all ${
                  index === currentImageIndex 
                    ? 'border-green-500 scale-105 shadow-lg' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={image.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl text-charcoal text-center mb-12">Our Outreach Programs</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Utensils,
                title: 'Food Pantry',
                description: 'Weekly distribution of fresh produce, groceries, and essential items to families experiencing food insecurity.'
              },
              {
                icon: Truck,
                title: 'Disaster Relief',
                description: 'Rapid response to natural disasters and emergencies, providing essential supplies and support to affected communities.'
              },
              {
                icon: Users,
                title: 'Community Events',
                description: 'Regular outreach events bringing resources, hope, and connection to underserved neighborhoods.'
              },
              {
                icon: MapPin,
                title: 'Mobile Pantry',
                description: 'Bringing food assistance directly to communities with limited access, reaching those who cannot come to us.'
              }
            ].map((program, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-gold/10 hover:border-green-200 transition-colors"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <program.icon size={24} className="text-green-600" />
                </div>
                <h3 className="font-serif text-xl text-charcoal mb-2">{program.title}</h3>
                <p className="text-charcoal/70">{program.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-green-700 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl mb-4">Support Our Mission</h2>
          <p className="text-white/80 mb-8">
            Your donations to Living Fruit Outreach directly support families in need. 
            As a 501(c)(3) nonprofit, all donations are tax-deductible.
          </p>
          <a 
            href="https://livingfruitoutreach.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-green-700 px-8 py-4 rounded-full font-semibold hover:bg-green-50 transition-colors"
          >
            <Heart size={20} />
            Donate at livingfruitoutreach.com
            <ExternalLink size={18} />
          </a>
        </div>
      </section>

      {/* Partnership Note */}
      <section className="py-12 px-6 bg-cream">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-charcoal/60 text-sm">
            Living Fruit Outreach is a 501(c)(3) nonprofit organization partnered with Living Fruit Ministry. 
            While both organizations share a common mission of serving our community, 
            Living Fruit Outreach operates independently with its own tax-exempt status.
          </p>
        </div>
      </section>
    </div>
  );
}
